import { forwardRef, useRef } from "react";
import { useMagneticCursor } from "@/hooks/useMagneticCursor";

/**
 * MagneticButton — botón con:
 *  1) atracción magnética muy sutil hacia el cursor (solo hover:hover)
 *  2) glow reactivo: un halo radial que sigue al puntero (via CSS vars)
 *  3) press feedback (via .press y whileTap si se pasa como motion)
 *
 * En touch/reduced-motion, degrada a botón estático: la clase `.magnetic`
 * omite el glow y el hook magnético no hace nada.
 */
const MagneticButton = forwardRef(function MagneticButton(
  {
    children,
    className = "",
    strength = 10,
    radius = 90,
    onClick,
    "data-testid": testId,
    disabled = false,
    type = "button",
    ...rest
  },
  externalRef
) {
  const innerRef = useRef(null);
  const wrapperRef = useRef(null);
  useMagneticCursor(wrapperRef, { strength, radius });

  const onPointerMove = (e) => {
    const el = innerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    el.style.setProperty("--glow-x", `${x}%`);
    el.style.setProperty("--glow-y", `${y}%`);
  };

  return (
    <span
      ref={wrapperRef}
      className="inline-block will-change-transform"
      style={{ display: "inline-block" }}
    >
      <button
        ref={(node) => {
          innerRef.current = node;
          if (typeof externalRef === "function") externalRef(node);
          else if (externalRef) externalRef.current = node;
        }}
        type={type}
        onClick={onClick}
        onPointerMove={onPointerMove}
        disabled={disabled}
        data-testid={testId}
        className={`press magnetic-glow ${className}`}
        {...rest}
      >
        {children}
      </button>
    </span>
  );
});

export default MagneticButton;
